import { Button, Checkbox, Group, Paper, Title } from "@mantine/core";
import { useCallback, useState, useEffect } from "react";
import api from "../../api";
import { useNotificationSettings } from "../../hooks/useNotificationSettings";
import { useToast } from "../../mantine/notifications/useToast";
import urls from "../../urls";
import type {
  NotificationSetting,
  NotificationSettings as NotificationSettingsState
} from "../../utils/backend-types";
import { NotificationSettingDbName } from "../../utils/backend-types";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import "./Settings.scss";

const NOTIFICATION_SETTINGS: NotificationSetting[] = [
  {
    dbName: NotificationSettingDbName.TodoCreation,
    label: "Wenn in einem meiner Projekte eine neue Aufgabe angelegt wurde."
  },
  {
    dbName: NotificationSettingDbName.TodoUpcomingDueDate,
    label: "Erinnerungen über Aufgaben, die bald fällig sind"
  }
];

function NotificationSettings() {
  const { showToast, showErrorToast } = useToast();
  const { settings, isLoading, error } = useNotificationSettings();

  const [currentSettings, setCurrentSettings] =
    useState<NotificationSettingsState>({} as NotificationSettingsState);
  const [inProgress, setInProgress] = useState(false);

  const onSettingChange = useCallback(
    (dbName: NotificationSettingDbName, checked: boolean) => {
      setCurrentSettings({ ...currentSettings, [dbName]: checked });
    },
    [currentSettings]
  );

  const handleSave = useCallback(async () => {
    setInProgress(true);

    try {
      await api.patch(urls.api.settings(), currentSettings);

      showToast({
        type: "success",
        message: "Die Einstellungen wurden erfolgreich gespeichert."
      });
    } catch (error) {
      showErrorToast(error);
    }

    setInProgress(false);
  }, [currentSettings, showToast, showErrorToast]);

  useEffect(() => {
    if (!isLoading) {
      setCurrentSettings(settings || ({} as NotificationSettingsState));
    }
  }, [settings, isLoading]);

  return (
    <Paper mb="lg">
      <Title variant="paper-header">E-Mail-Benachrichtigungen</Title>
      <LoadOrError
        error={error}
        loading={isLoading}
        loadingMessage="Einstellungen werden geladen"
      >
        {NOTIFICATION_SETTINGS.map(({ dbName, label }) => (
          <Checkbox
            checked={!!currentSettings?.[dbName]}
            disabled={inProgress}
            key={dbName}
            label={label}
            onChange={(event) =>
              onSettingChange(dbName, event.currentTarget.checked)
            }
          />
        ))}
        <Group variant="paper-footer">
          <Button
            color="secondary"
            loading={inProgress}
            type="submit"
            onClick={handleSave}
          >
            Speichern
          </Button>
        </Group>
      </LoadOrError>
    </Paper>
  );
}

export { NotificationSettings };
